/**
 * Application Insights loader.
 *
 * The SDK is imported dynamically and only after consent is granted, so it stays
 * out of the initial bundle entirely for visitors who decline. If no connection
 * string was supplied at build time the whole module is inert — which is the state
 * the site shipped in until now.
 *
 * Set VITE_APPINSIGHTS_CONNECTION_STRING at build time. The connection string is
 * not a secret: it is an ingestion key that is embedded in client JavaScript by
 * design, so it belongs in a GitHub Actions *variable*, not a secret.
 */
import type { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { getConsent } from "./consent";

let client: ApplicationInsights | null = null;
let pending: Promise<ApplicationInsights | null> | null = null;

function connectionString(): string {
  return (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING ?? "").trim();
}

/** True when a connection string was baked in at build time. */
export function isConfigured(): boolean {
  return connectionString().length > 0;
}

/**
 * Resolves the App Insights client, loading the SDK on first use. Returns null
 * when analytics are not configured or consent has not been granted — callers
 * treat null as "drop the event".
 */
export function getClient(): Promise<ApplicationInsights | null> {
  if (client) return Promise.resolve(client);
  if (!isConfigured() || getConsent() !== "granted") return Promise.resolve(null);
  if (typeof window === "undefined") return Promise.resolve(null);

  pending ??= import("@microsoft/applicationinsights-web")
    .then((mod) => {
      const inst = new mod.ApplicationInsights({
        config: {
          connectionString: connectionString(),
          // React Router drives navigation; auto route tracking double-counts and
          // misses the initial view, so page views are sent explicitly instead.
          enableAutoRouteTracking: false,
          autoTrackPageVisitTime: true,
          disableFetchTracking: false,
          enableCorsCorrelation: false,
        },
      });
      inst.loadAppInsights();
      client = inst;
      return inst;
    })
    .catch(() => {
      // A blocked or failed SDK load must never break the page.
      pending = null;
      return null;
    });

  return pending;
}

/** Test-only: forgets the loaded client. */
export function _resetClient(): void {
  client = null;
  pending = null;
}
